import type {FormatSpec} from '../spec/SpecTypes';

/**
 * ChartSpec - a plain-JSON, versioned description of a configurable chart over rows of data.
 *
 * Like GridSpec, it is the single source of truth shared by the chart configurator, the AI chart
 * assistant and the chart's own interactions (legend clicks). All changes are ChartSpecOps (see
 * ChartSpecOps.ts) applied by one reducer; ChartSpecBuilder turns the spec plus the rows into a
 * Highcharts config. The spec maps catalog fields to the chart, so it can plot any numeric field
 * the host provides - the host supplies raw rows, not pre-built series.
 *
 * Two layouts:
 *  - 'series': one point per row. `xField` gives the x value (a date, number or category), and each
 *    series plots one numeric field. A pie uses `xField` for slice names and the first visible series.
 *    A candle chart draws one candle per row from the `ohlc` fields, with the series as lines over it.
 *  - 'categories': one point per field of the *first* row - for single-row data such as a summary.
 *    Each series entry is one category (a bar or a slice).
 *
 * Everything here must stay JSON-serializable - no functions. Colors and formats are named
 * descriptors, resolved in ChartSpecBuilder.ts / spec/Formats.ts.
 *
 * NOTE: the AI chart assistant's output schema, chartSpecOps.schema.json, describes these types and the ops in
 * ChartSpecOps.ts; the client sends it with each request (rzx-backend/docs/decisions/0027). Keep it in step with
 * any change here. Bump CHART_SPEC_VERSION on any breaking change, and add an upgrade step to normalizeSpec().
 */
export const CHART_SPEC_VERSION = 1;

export const CHART_TYPES = ['line', 'spline', 'area', 'areaspline', 'column', 'bar', 'scatter', 'pie', 'candle'] as const;
export type ChartType = (typeof CHART_TYPES)[number];

/** How a candle chart draws each row's open/high/low/close - 'hlc' has no open. */
export const CANDLE_STYLES = ['candlestick', 'hollowcandlestick', 'heikinashi', 'ohlc', 'hlc'] as const;
export type CandleStyle = (typeof CANDLE_STYLES)[number];

export const OHLC_KEYS = ['open', 'high', 'low', 'close'] as const;
export type OhlcKey = (typeof OHLC_KEYS)[number];

/** Catalog fields - numbers - giving each row's open, high, low and close. */
export type ChartSpecOhlc = Record<OhlcKey, string>;

/** Types a single series may use instead of the chart's type ('series' layout, not for pie or bar charts). */
export const SERIES_TYPES = ['line', 'spline', 'area', 'areaspline', 'column', 'scatter'] as const;
export type SeriesType = (typeof SERIES_TYPES)[number];

export const CHART_LAYOUTS = ['series', 'categories'] as const;
export type ChartLayout = (typeof CHART_LAYOUTS)[number];

export const SERIES_COLORS = ['blue', 'orange', 'red', 'teal', 'green', 'yellow', 'purple', 'pink', 'brown', 'gray'] as const;
export type SeriesColor = (typeof SERIES_COLORS)[number];

export const LEGEND_POSITIONS = ['none', 'bottom', 'top', 'right'] as const;
export type LegendPosition = (typeof LEGEND_POSITIONS)[number];

export const STACKINGS = ['none', 'normal', 'percent'] as const;
export type Stacking = (typeof STACKINGS)[number];

/** What a data label shows: nothing, the point's name (slice, category or series), its value, or both. */
export const DATA_LABELS = ['none', 'name', 'value', 'nameAndValue'] as const;
export type DataLabels = (typeof DATA_LABELS)[number];

/** Largest pie hole, as a percentage of the pie - leaves a visible ring. */
export const MAX_INNER_SIZE = 90;

export const AXIS_IDS = ['x', 'primary', 'secondary'] as const;
export type AxisId = (typeof AXIS_IDS)[number];

export interface ChartSpecSeries {
    /** Catalog field - a number. Also serves as the series id. */
    field: string;
    /** Legend / category name - defaults to the field's label. */
    name?: string;
    /** 'series' layout only - overrides the chart type for this series. */
    type?: SeriesType;
    /** 'series' layout only - the y axis to plot against. Omit for 'primary'. */
    axis?: 'primary' | 'secondary';
    color?: SeriesColor;
    hidden?: boolean;
    /** 'series' layout only - draw a dashed line. */
    dashed?: boolean;
}

export interface ChartSpecAxis {
    title?: string;
    /** Format of the axis labels, and of the tooltip and data labels of values plotted against it. */
    format?: FormatSpec;
    min?: number;
    max?: number;
}

export interface ChartSpecAxes {
    x: ChartSpecAxis;
    primary: ChartSpecAxis;
    /** Shown only when a visible series uses it. */
    secondary: ChartSpecAxis;
}

export interface ChartSpec {
    version: number;
    type: ChartType;
    layout: ChartLayout;
    /** 'series' layout - the field giving each row's x value. Null in the 'categories' layout. */
    xField: string;
    series: ChartSpecSeries[];
    /** Candle only - the fields each candle is drawn from. Null until the chart is first made a candle chart. */
    ohlc: ChartSpecOhlc;
    /** Candle only. */
    candleStyle: CandleStyle;
    axes: ChartSpecAxes;
    legend: LegendPosition;
    /** Column, bar and area charts. */
    stacking: Stacking;
    /** Labels on the points (pie slices get a callout). */
    dataLabels: DataLabels;
    /** One tooltip for all series at the hovered x value. */
    sharedTooltip: boolean;
    /** Pie only - the hole in the middle as a percentage of the pie: 0 is a full pie, higher is a thinner ring. */
    innerSize: number;
    /** Point markers on lines. */
    markers: boolean;
}

/** What the host chart supports - drives the configurator, what the AI is told it may do, and validation. */
export interface ChartSpecCapabilities {
    /** Layouts the host's data supports - 'categories' needs a single row. */
    layouts: ChartLayout[];
}

export const DEFAULT_CHART_SPEC: Omit<ChartSpec, 'series'> = {
    version: CHART_SPEC_VERSION,
    type: 'line',
    layout: 'series',
    xField: null,
    ohlc: null,
    candleStyle: 'candlestick',
    axes: {x: {}, primary: {}, secondary: {}},
    legend: 'bottom',
    stacking: 'none',
    dataLabels: 'none',
    sharedTooltip: true,
    innerSize: 0,
    markers: true
};
